import * as DocumentPicker from "expo-document-picker";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/storage";
import _ from "lodash";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { API_URL } from "src/env";
import { getSafeUrl, safeMap } from "src/util/functions";
import {
  tAnnouncementRequestBody,
  tBoutique,
  tBrand,
  tBuyerData,
  tHeroImage,
  tProduct,
  tPromotion,
  tRep,
  tRetailerApplication,
  tUser,
  tVendorData,
  validate,
} from "tmt_api/src/types/types.validator";
import api from "../../util/api";
import { handleErrorEvents } from "../errorHandler/actions";
import { tAccountType, tCompanyState, tSetCompany } from "./types";
import { v4 as uuidv4 } from "uuid";
import { tAdminCompanyState } from "../AdminSettings/types";
import { AxiosError } from "axios";
import {
  getApiError,
  isApiError,
  postBrandAnnouncement,
  postBrandRetailer,
  postBrandSetupAccount,
  postProduct,
  postProductActivate,
  postProductDeactivate,
  postPromotion,
  postRepRetailer,
  postRepSetupAccount,
  postRetailerLegacySetupTerms,
  postRetailerSetupRewards,
  putBrand,
  putBrandAcceptTerms,
  putBrandAnnouncement,
  putProduct,
  putPromotion,
  putRep,
  putRetailer,
  putRetailerAcceptTerms,
} from "src/util/apiNew";
import { tReduxAction } from "../types";
import { useAppDispatch } from "..";

export const setCompany = (myData: tCompanyState): tSetCompany => {
  return {
    type: "SET_COMPANY",
    company: myData,
  };
};

export const uploadBannerImage = (myId: string | false, file?: File): tReduxAction => {
  return (dispatch) => {
    Promise.resolve()
      .then(() => {
        if (file) {
          const kFileExt = file.type.replace(/^.*\//gm, "");
          const aImageRef = firebase.storage().ref(`brands/${myId}/uploads/banner.${kFileExt}`);
          return aImageRef.put(file);
        } else {
          throw new Error("brand id is undefined");
        }
      })
      .then((myUploadResults) => {
        return myUploadResults.ref.getDownloadURL();
      })
      .then((url) => {
        dispatch({
          type: "ADMIN_UPDATE_BRAND_SETTINGS",
          settingsPart: { images: { bannerDownloadURL: url } },
        });
      })
      .catch((e: Error) => {
        dispatch(handleErrorEvents(e.message, "error", e));
      });
  };
};

export const uploadLogoImage = (
  myId: string | false,
  myType: tAdminCompanyState["type"],
  file?: File,
): tReduxAction => {
  return (dispatch) => {
    if (myType === "none") {
      throw new Error("Invalid user type: none");
    }
    Promise.resolve()
      .then(() => {
        if (file) {
          let aImageRef: firebase.storage.Reference;
          const kFileExt = file.type.replace(/^.*\//gm, "");
          if (myType === "brand") {
            aImageRef = firebase.storage().ref(`brands/${myId}/uploads/profile.${kFileExt}`);
          } else if (myType === "rep") {
            aImageRef = firebase.storage().ref(`rep/${myId}/uploads/profile.${kFileExt}`);
          } else if (myType === "tmtAdmin") {
            aImageRef = firebase.storage().ref(`tmtAdmin/uploads/profile.${kFileExt}`);
          } else {
            aImageRef = firebase.storage().ref(`retailer/${myId}/uploads/profile.${kFileExt}`);
          }
          return aImageRef.put(file);
        } else {
          throw new Error("brand id is undefined");
        }
      })
      .then((myUploadResults) => {
        dispatch({ type: "SET_UPLOADING_PROFILE_PIC", loading: true });
        return myUploadResults.ref.getDownloadURL();
      })
      .then((url) => {
        if (myType === "brand") {
          dispatch({
            type: "ADMIN_UPDATE_BRAND_SETTINGS",
            settingsPart: { images: { logoDownloadURL: url } },
          });
        } else if (myType === "rep") {
          dispatch({
            type: "ADMIN_UPDATE_REP_SETTINGS",
            settingsPart: { avatar: url },
          });
        } else if (myType === "retailer") {
          dispatch({
            type: "ADMIN_UPDATE_RETAIL_SETTINGS",
            settingsPart: { avatar: url },
          });
        }
      })
      .then(() => {
        dispatch({ type: "SET_UPLOADING_PROFILE_PIC", loading: false });
      })
      .catch((e: Error) => {
        console.error(e);
        dispatch(handleErrorEvents(e.message, "error", e));
      });
  };
};

export const uploadCarouselImage = ({
  id,
  images,
  file,
}: {
  id?: string;
  images?: tHeroImage[];
  file?: File;
}): tReduxAction => {
  return (dispatch) => {
    return Promise.resolve()
      .then(() => {
        if (id && file) {
          const uuid = uuidv4();
          const kFileExt = file.type.replace(/^.*\//gm, "");
          const aImageRef = firebase.storage().ref(`brands/${id}/uploads/hero${uuid}.${kFileExt}`);
          return aImageRef.put(file);
        } else {
          throw new Error("brand id is undefined");
        }
      })
      .then((myUploadResults) => {
        return myUploadResults.ref.getDownloadURL();
      })
      .then((url) => {
        const clonned = _.cloneDeep(images ?? []);
        clonned.push({ downloadURL: url, isPrimary: false });
        dispatch({ type: "ADMIN_UPDATE_HERO_IMAGES", images: clonned });
      })
      .catch((e: Error) => {
        dispatch(handleErrorEvents(e.message, "error", e));
      });
  };
};

export const replaceCarouselImage = ({
  id,
  index,
  images,
  file,
}: {
  id?: string;
  index: number;
  images?: tHeroImage[];
  file?: File;
}): tReduxAction => {
  return (dispatch) => {
    return Promise.resolve()
      .then(() => {
        if (id && file) {
          const uuid = uuidv4();
          const kFileExt = file.type.replace(/^.*\//gm, "");
          const aImageRef = firebase.storage().ref(`brands/${id}/products/hero${uuid}.${kFileExt}`);
          return aImageRef.put(file);
        } else {
          throw new Error("brand id is undefined");
        }
      })
      .then((myUploadResults) => {
        return myUploadResults.ref.getDownloadURL();
      })
      .then((url) => {
        const clonned = _.cloneDeep(images ?? []);
        clonned.splice(index, 1, { downloadURL: url, isPrimary: false });
        dispatch({ type: "ADMIN_UPDATE_HERO_IMAGES", images: clonned });
      })
      .catch((e: Error) => {
        dispatch(handleErrorEvents(e.message, "error", e));
      });
  };
};

export const deleteCarouselImage = ({ index, images }: { index: number; images?: tHeroImage[] }): tReduxAction => {
  return (dispatch) => {
    const clonned = _.cloneDeep(images ?? []).filter((_image, imageIndex) => index !== imageIndex);
    dispatch({ type: "ADMIN_UPDATE_HERO_IMAGES", images: clonned });
  };
};

export const uploadTermsAndConditions = (myId: string | false, myType: tAdminCompanyState["type"]): tReduxAction => {
  return (dispatch) => {
    if (myType === "brand") {
      const aImageRef = firebase.storage().ref(`brands/${myId}/uploads/terms_and_conditions.pdf`);
      const aProfileResult = (name: string, file: File) =>
        aImageRef
          .put(file)
          .then((myUploadResults) => {
            return myUploadResults.ref.getDownloadURL();
          })
          .then((url) => {
            return dispatch({
              type: "ADMIN_UPDATE_BRAND_SETTINGS",
              settingsPart: { termsAndConditions: { name, url } },
            });
          });
      DocumentPicker.getDocumentAsync({ type: "application/pdf" })
        .then((myUploadedPdf) => {
          if (myUploadedPdf.type === "success" && myUploadedPdf.file) {
            dispatch({
              type: "RIBBON",
              message: "File Uploaded! Be sure to Save before leaving page.",
            });
            aProfileResult(myUploadedPdf.name, myUploadedPdf.file);
          } else {
            dispatch(handleErrorEvents("Brand ID is undefined", "error", new Error("brand id is undefined")));
          }
        })
        .catch((e: Error) => {
          dispatch(handleErrorEvents(e.message, "error", e));
        });
    }
  };
};

export const uploadRetailTaxInfo = (myId: string | false, myType: tAdminCompanyState["type"]): tReduxAction => {
  return (dispatch) => {
    if (myType === "retailer") {
      const aImageRef = firebase.storage().ref(`retailer/${myId}/uploads/tax_info.pdf`);
      const aProfileResult = (name: string, file: File) =>
        aImageRef
          .put(file)
          .then((myUploads) => {
            return myUploads.ref.getDownloadURL();
          })
          .then((url) => {
            return dispatch({ type: "ADMIN_UPDATE_RETAIL_SETTINGS", settingsPart: { taxInfo: { name, url } } });
          });
      DocumentPicker.getDocumentAsync({ type: "application/pdf" })
        .then((myUploadPdf) => {
          if (myUploadPdf.type === "success" && myUploadPdf.file) {
            dispatch({
              type: "RIBBON",
              message: "Successfully uploaded Tax Info. Be sure to Save before leaving page.",
            });
            aProfileResult(myUploadPdf.name, myUploadPdf.file);
          } else {
            dispatch(handleErrorEvents("Retailer ID is undefined", "error", new Error("Retailer id is undefined")));
          }
        })
        .catch((e: Error) => {
          dispatch(handleErrorEvents(e.message, "error", e));
        });
    }
  };
};

export const setupRetailerTerms = (data: tBuyerData): tReduxAction<Promise<string>> => {
  return (dispatch) =>
    postRetailerLegacySetupTerms(data)
      .then((res) => {
        dispatch({ type: "RIBBON", message: "Terms setup" });
        return res;
      })
      .catch((error) => {
        const apiError = getApiError(error);
        dispatch({ type: "RIBBON", message: new Error(apiError.message) });
        throw error;
      });
};

export const setupBrandAccount = (data: tVendorData): tReduxAction => {
  return (dispatch) =>
    postBrandSetupAccount(data)
      .then(() => dispatch({ type: "RIBBON", message: "Terms setup" }))
      .catch((error) => {
        const apiError = getApiError(error);
        dispatch({ type: "RIBBON", message: new Error(apiError.message) });
        throw error;
      });
};

export const setupRepAccount = (data: tVendorData): tReduxAction => {
  return (dispatch) =>
    postRepSetupAccount(data)
      .then(() => dispatch({ type: "RIBBON", message: "Terms setup" }))
      .catch((error) => {
        const apiError = getApiError(error);
        dispatch({ type: "RIBBON", message: new Error(apiError.message) });
        throw error;
      });
};

export const setupRetailerRewards = (data: tVendorData): tReduxAction => {
  return (dispatch) =>
    postRetailerSetupRewards(data)
      .then(() => dispatch({ type: "RIBBON", message: "Terms setup" }))
      .catch((error) => {
        const apiError = getApiError(error);
        dispatch({ type: "RIBBON", message: new Error(apiError.message) });
        throw error;
      });
};

export const saveSettingsPage = (myCompany: tAdminCompanyState): tReduxAction => {
  return async (dispatch, getState) => {
    const { profile, isLoggedIn } = getState().UserReducer;
    dispatch({ type: "SET_UPDATING_PROFILE", loading: true });

    if (!isLoggedIn) {
      return undefined;
    }

    if (profile.accountType === "brand" && myCompany.type === "brand") {
      await dispatch(saveBrandPage(myCompany, profile));
    } else if (profile.accountType === "retailer" && myCompany.type === "retailer") {
      await dispatch(saveRetailPage(myCompany, profile));
    } else if (profile.accountType === "rep" && myCompany.type === "rep") {
      await dispatch(saveRepPage(myCompany, profile));
    }

    return undefined;
  };
};

export const saveBrandPage = (companyState: tAdminCompanyState, user: tUser): tReduxAction => {
  return (dispatch) => {
    if (companyState.type === "brand") {
      return Promise.resolve()
        .then(() => {
          const brandProfile = _.omit(companyState, "type");
          brandProfile.website = getSafeUrl(brandProfile.website);
          brandProfile.links = _.mapValues(brandProfile.links, getSafeUrl);
          return validate("tBrand")(brandProfile);
        })
        .then((brandProfile) => {
          return putBrand(brandProfile.id, { brand: brandProfile, user });
        })
        .then(() => {
          dispatch({
            type: "RIBBON",
            message: "Successfully updated brand settings",
          });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        })
        .catch((e: Error) => {
          if (isApiError(e) && e.response && e.response.status < 500) {
            dispatch({ type: "RIBBON", message: new Error(e.response.data.message) });
          } else {
            dispatch(handleErrorEvents(e.message, "error", e));
          }
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
          throw e;
        });
    }
    return Promise.resolve();
  };
};

export const updateBrandProfile = (brand: tBrand, repUser: tUser): tReduxAction => {
  return async (dispatch) => {
    try {
      await putBrand(brand.id, { brand, user: repUser });
      dispatch({ type: "RIBBON", message: "Successfully updated brand settings" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }
  };
};

export const saveRetailPage = (companyState: tAdminCompanyState, user: tUser): tReduxAction => {
  return (dispatch) => {
    if (companyState.type === "retailer") {
      Promise.resolve()
        .then(() => {
          const retailProfile = _.omit(companyState, "type");
          retailProfile.website = getSafeUrl(retailProfile.website);
          retailProfile.links = _.mapValues(retailProfile.links, getSafeUrl);
          return validate("tBoutique")(retailProfile);
        })
        .then((retailProfile) => putRetailer(retailProfile.id, { retailer: retailProfile, user }))
        .then(() => {
          dispatch({
            type: "RIBBON",
            message: "Successfully updated retail settings",
          });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        })
        .catch((e) => {
          if (isApiError(e) && e.response && e.response.status < 500) {
            dispatch({ type: "RIBBON", message: new Error(e.response.data.message) });
          } else {
            dispatch(handleErrorEvents(e.message, "error", e));
          }
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        });
    }
  };
};

export const saveRepPage = (companyState: tAdminCompanyState, user: tUser): tReduxAction => {
  return (dispatch) => {
    if (companyState.type === "rep") {
      Promise.resolve()
        .then(() => {
          const repProfile = _.omit(companyState, "type");
          repProfile.socialMedia = _.mapValues(repProfile.socialMedia, getSafeUrl);
          return validate("tRep")(repProfile);
        })
        .then((repProfile) => putRep(repProfile.id, { rep: repProfile, user }))
        .then(() => {
          dispatch({
            type: "RIBBON",
            message: "Successfully updated rep settings",
          });
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        })
        .catch((e) => {
          if (isApiError(e) && e.response && e.response.status < 500) {
            dispatch({ type: "RIBBON", message: new Error(e.response.data.message) });
          } else {
            dispatch(handleErrorEvents(e.message, "error", e));
          }
          dispatch({ type: "SET_UPDATING_PROFILE", loading: false });
        });
    }
  };
};

export const updateRepProfile = (rep: tRep, repUser: tUser): tReduxAction => {
  return async (dispatch) => {
    try {
      await putRep(rep.id, { rep, user: repUser });
      dispatch({ type: "RIBBON", message: "Successfully updated rep settings" });
    } catch (error) {
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }
  };
};

export const fetchCompanyData = (
  myCompanySnapshot: firebase.firestore.DocumentSnapshot,
  myAccountType: tAccountType,
): tReduxAction => {
  return (dispatch) => {
    try {
      if (!myCompanySnapshot.exists) {
        console.warn("Couldn't find the company!");
        return undefined;
      }

      if (myAccountType === "brand") {
        const brandData = _.merge<tBrand, { type: "brand" }>(validate("tBrand")(myCompanySnapshot.data()), {
          type: "brand",
        });
        dispatch({
          type: "SET_COMPANY",
          company: brandData,
        });
      } else if (myAccountType === "retailer") {
        const retailerData = _.merge<tBoutique, { type: "retailer" }>(validate("tBoutique")(myCompanySnapshot.data()), {
          type: "retailer",
        });
        dispatch({
          type: "SET_COMPANY",
          company: retailerData,
        });
      } else if (myAccountType === "rep") {
        const repData = _.merge<tRep, { type: "rep" }>(validate("tRep")(myCompanySnapshot.data()), { type: "rep" });
        dispatch({
          type: "SET_COMPANY",
          company: repData,
        });
      }

      dispatch({
        type: "SET_LOADING_COMPANY",
        loading: false,
      });
    } catch (error) {
      console.warn(error);
    }
    return undefined;
  };
};

export const useFetchCompany = (): void => {
  const dispatch = useAppDispatch();
  const aCompanyRefPath = useSelector(({ UserReducer }) => UserReducer.profile.companyRefPath);
  const aAccountType = useSelector(({ UserReducer }) => UserReducer.profile.accountType);
  useEffect(() => {
    if (!aCompanyRefPath) {
      dispatch({ type: "RESET_SETTINGS" });
      return undefined;
    }

    dispatch({
      type: "SET_LOADING_COMPANY",
      loading: true,
    });

    return firebase
      .firestore()
      .doc(aCompanyRefPath)
      .onSnapshot((myCompanySnapshot) => {
        dispatch(fetchCompanyData(myCompanySnapshot, aAccountType));
      });
  }, [aCompanyRefPath, aAccountType, dispatch]);
};

export const useFetchPromotions = (): void => {
  const dispatch = useAppDispatch();
  const aCompanyRefPath = useSelector(({ UserReducer }) => UserReducer.profile.companyRefPath);
  const aAccountType = useSelector(({ UserReducer }) => UserReducer.profile.accountType);
  const rCompanyId = useSelector(({ CompanyReducer }) => (CompanyReducer.type === "brand" ? CompanyReducer.id : ""));
  useEffect(() => {
    if (!aCompanyRefPath) {
      dispatch({ type: "RESET_SETTINGS" });
      return undefined;
    }

    dispatch({
      type: "SET_LOADING_COMPANY",
      loading: true,
    });
    if (aAccountType !== "brand") {
      return undefined;
    }
    return firebase
      .firestore()
      .collection("promotions")
      .where("brandId", "==", rCompanyId)
      .onSnapshot((myCompanySnapshot) => {
        dispatch({
          type: "UPDATE_BRAND_PROMOTIONS",
          settingsPart: safeMap(myCompanySnapshot.docs, validate("tPromotion")),
        });
        dispatch({
          type: "SET_LOADING_COMPANY",
          loading: false,
        });
      });
  }, [aCompanyRefPath, aAccountType, dispatch, rCompanyId]);
};

export const uploadImage = (folderPath: string, fileName: string): Promise<string> =>
  Promise.all([
    firebase.storage().ref(folderPath).listAll(),
    DocumentPicker.getDocumentAsync({ type: "image/png, image/jpeg, image/webp" }),
  ])
    .then(([_myImages, myUploadedImage]) => {
      if (myUploadedImage.type === "success" && myUploadedImage.file) {
        const kFileExt = myUploadedImage.file.type.replace(/^.*\//gm, "");
        const uuid = uuidv4();
        const aImageRef = firebase.storage().ref(`${folderPath}/${fileName}${uuid}.${kFileExt}`);
        return aImageRef.put(myUploadedImage.file);
      }
      throw new Error("brand id is undefined");
    })
    .then((myUploadResults) => myUploadResults.ref.getDownloadURL());

export const addPromotion = (promotion: tPromotion): tReduxAction => {
  return async () => {
    return postPromotion(promotion);
  };
};

export const cancelPromotion = (promotionId: string, promotion: tPromotion): tReduxAction => {
  return async () => {
    const updatedPromotion = _.cloneDeep(promotion);
    updatedPromotion.deactivated = true;
    return putPromotion(promotionId, updatedPromotion);
  };
};

export const updatePromotion = (promotionId: string, promotion: tPromotion): tReduxAction => {
  return async () => {
    return putPromotion(promotionId, promotion);
  };
};

export const bulkUploadProducts =
  (documentResult: DocumentPicker.DocumentResult): tReduxAction<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      const { accountType, companyRefPath } = getState().UserReducer.profile;
      if (accountType !== "brand") {
        dispatch(handleErrorEvents("You cannot upload products!", "error", new Error("You cannot upload products!")));
        return;
      }
      const storage = firebase.storage();
      if (API_URL.includes("localhost")) {
        storage.useEmulator("localhost", 9199);
      }
      const recordPath = `${companyRefPath}/productSheets`;

      if (
        documentResult.type !== "success" ||
        !documentResult.name ||
        !String(documentResult.name).endsWith(".xlsx") ||
        !documentResult.file
      ) {
        dispatch({
          type: "RIBBON",
          message: new Error("Failed! You must upload an excel spreadsheet with a .xlsx extension!"),
        });
        return;
      }

      const sheetName = documentResult.name;
      const sheetRef = storage.ref(recordPath).child(sheetName);
      const productSheetId = sheetRef.fullPath;

      await sheetRef.put(documentResult.file);

      if (!productSheetId) throw new Error("Invalid product sheet Id!");
      await api.post({ endpoint: "/products/upload", body: { productSheetId } });

      dispatch({ type: "RIBBON", message: `Successfully Uploaded! ${sheetName}` });
    } catch (e) {
      const error = getApiError(e);
      if (e instanceof Error) dispatch(handleErrorEvents(`Error! ${e.message} | ${e.stack}`, "error", e));
      throw error;
    }
  };

type tError = {
  status: number;
  message: string;
};

export const addProduct = (): tReduxAction => (dispatch, getState) => {
  const { accountType: rAccountType, companyRefPath: rCompanyRefPath } = getState().UserReducer.profile;
  const rCompany = getState().CompanyReducer;
  const rProduct = getState().ProductReducer;
  const tempProduct = _.cloneDeep(rProduct);
  if (rAccountType === "brand" && rCompanyRefPath && rCompany.type === "brand") {
    tempProduct.brandId = rCompany.brandId ?? 0;
    tempProduct.brandUid = rCompany.id;
    tempProduct.brandName = rCompany.name;

    tempProduct.timeCreated = firebase.firestore.Timestamp.now();
    tempProduct.timeUpdated = firebase.firestore.Timestamp.now();

    dispatch({ type: "SET_SUBMITTING", payload: true });
    return api
      .post({ endpoint: `/products` as const, body: tempProduct })
      .then(() => {
        dispatch(handleErrorEvents("Successfully created product", "success"));
      })
      .catch((error: AxiosError<tError>) => {
        if (error.isAxiosError) {
          error.message = error.response?.data?.message ?? error.message;
        }
        dispatch(handleErrorEvents(error.message, "error", error));
        throw error;
      })
      .finally(() => {
        dispatch({ type: "RESET_FORM_STATE" });
      });
  }

  return Promise.resolve();
};

export const updateProduct = (): tReduxAction => (dispatch, getState) => {
  const { accountType: rAccountType, companyRefPath: rCompanyRefPath } = getState().UserReducer.profile;
  const rCompany = getState().CompanyReducer;
  const rProduct = getState().ProductReducer;
  const tempProduct = _.cloneDeep(rProduct);

  if (rAccountType === "brand" && rCompanyRefPath && rCompany.type === "brand") {
    tempProduct.timeUpdated = firebase.firestore.Timestamp.now();

    dispatch({ type: "SET_SUBMITTING", payload: true });
    return api
      .put({
        endpoint: `/products/update` as const,
        body: tempProduct,
      })
      .then(() => {
        dispatch(handleErrorEvents("Successfully updated product", "success"));
      })
      .catch((error: AxiosError<tError>) => {
        if (error.isAxiosError) {
          error.message = error.response?.data?.message ?? error.message;
        }
        dispatch(handleErrorEvents(error.message, "error", error));
        throw error;
      })
      .finally(() => {
        dispatch({ type: "RESET_FORM_STATE" });
      });
  }

  return Promise.resolve();
};

export const createBrandProduct = (product: tProduct): tReduxAction => {
  return async (dispatch) => {
    await postProduct(product);
    dispatch({ type: "RIBBON", message: "Successfully created product" });
  };
};

export const updateBrandProduct = (product: tProduct): tReduxAction => {
  return async (dispatch) => {
    await putProduct(product);
    dispatch({ type: "RIBBON", message: "Successfully updated product" });
  };
};

export const activateProduct = (uid: string): tReduxAction => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_LOADING_PRODUCT", loading: true });
      await postProductActivate(uid);
    } catch (e) {
      const error = getApiError(e);
      dispatch({ type: "RIBBON", message: new Error(error.message) });
    } finally {
      dispatch({ type: "SET_LOADING_PRODUCT", loading: false });
    }
  };
};

export const deactivateProduct = (uid: string): tReduxAction => {
  return async (dispatch) => {
    try {
      dispatch({ type: "SET_LOADING_PRODUCT", loading: true });
      await postProductDeactivate(uid);
    } catch (e) {
      const error = getApiError(e);
      dispatch({ type: "RIBBON", message: new Error(error.message) });
    } finally {
      dispatch({ type: "SET_LOADING_PRODUCT", loading: false });
    }
  };
};

export const acceptTerms = (): tReduxAction => {
  return async (dispatch, getState) => {
    try {
      const { accountType } = getState().UserReducer.profile;

      if (accountType === "brand") await putBrandAcceptTerms();
      else if (accountType === "retailer") await putRetailerAcceptTerms();
    } catch (e) {
      const error = getApiError(e);
      dispatch({ type: "RIBBON", message: new Error(error.message) });
    }
  };
};

export const brandAddRetailer = (
  brandId: string,
  data: tRetailerApplication,
): tReduxAction<Promise<{ isExistingUser: boolean; isConnected: boolean }>> => {
  return async () => {
    return postBrandRetailer(brandId, data);
  };
};

export const brandAddAnnouncement = (brandId: string, data: tAnnouncementRequestBody): tReduxAction<Promise<void>> => {
  return async () => {
    return postBrandAnnouncement(brandId, data);
  };
};

export const brandEditAnnouncement = (
  brandId: string,
  announcementId: string,
  data: tAnnouncementRequestBody,
): tReduxAction<Promise<void>> => {
  return async () => {
    return putBrandAnnouncement(brandId, announcementId, data);
  };
};

export const repAddRetailer = (
  repId: string,
  data: tRetailerApplication,
): tReduxAction<Promise<{ isExistingUser: boolean; isConnected: boolean }>> => {
  return async () => {
    return postRepRetailer(repId, data);
  };
};
